import { apiFetch } from '@/lib/api';

export interface Airport {
  iata: string;
  name: string;
  type: string;
  iso_country: string;
  lat: number;
  lon: number;
  distance_km?: number;
}

export interface ImportResult {
  success: boolean;
  processed: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: Array<{
    row: number;
    field?: string;
    message: string;
  }>;
  stats: {
    by_type: Record<string, number>;
    by_country: Record<string, number>;
  };
}

export interface ValidationResult {
  isValid: boolean;
  totalRows: number;
  validRows: number;
  mediumLargeAirports: number;
  errors: Array<{
    row: number;
    field?: string;
    message: string;
  }>;
  sample: Array<any>;
}

export interface AirportStats {
  total: number;
  by_type: Record<string, number>;
  by_country: Record<string, number>;
  with_iata: number;
  with_scheduled_service: number;
}

export interface Paginated<T> {
  items: T[];
  pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
}

export class AirportsService {
  async getStats(): Promise<AirportStats> {
    const res = await apiFetch('/api/admin/airports/stats');
    if (!res.ok) throw new Error('Failed to fetch airport statistics');
    const data = await res.json();
    return data.data as AirportStats;
  }

  async searchByCity(query: string, limit = 5): Promise<Airport[]> {
    const params = new URLSearchParams({
      query: query.trim(),
      limit: limit.toString()
    });
    
    const res = await apiFetch(`/api/admin/airports/search-by-city?${params}`);
    if (!res.ok) throw new Error('Failed to search airports by city');
    const data = await res.json();
    return data.data.airports as Airport[];
  }

  async validateCSV(file: File): Promise<ValidationResult> {
    const formData = new FormData();
    formData.append('csvFile', file);
    
    const res = await apiFetch('/api/admin/airports/validate-csv', {
      method: 'POST',
      body: formData,
      // Note: Don't set Content-Type header, let browser set it for FormData
    });
    
    if (!res.ok) throw new Error('Failed to validate CSV file');
    const data = await res.json();
    return data.data as ValidationResult;
  }

  async importCSV(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('csvFile', file);
    
    const res = await apiFetch('/api/admin/airports/import', {
      method: 'POST',
      body: formData,
    });
    
    if (!res.ok) throw new Error('Failed to import CSV file');
    const data = await res.json();
    return data.data as ImportResult;
  }

  async list(
    page = 1,
    pageSize = 50,
    filters: {
      type?: string[];
      country?: string;
      scheduled_service?: boolean;
    } = {}
  ): Promise<Paginated<Airport>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    
    if (filters.type && filters.type.length > 0) {
      filters.type.forEach(t => params.append('type', t));
    }
    if (filters.country) {
      params.append('country', filters.country);
    }
    if (typeof filters.scheduled_service === 'boolean') {
      params.append('scheduled_service', filters.scheduled_service.toString());
    }
    
    const res = await apiFetch(`/api/admin/airports?${params}`);
    if (!res.ok) throw new Error('Failed to fetch airports');
    const data = await res.json();
    return data.data as Paginated<Airport>;
  }

  async getByIata(iata: string): Promise<Airport> {
    const res = await apiFetch(`/api/admin/airports/${iata}`);
    if (!res.ok) throw new Error(`Failed to fetch airport ${iata}`);
    const data = await res.json();
    return data.data as Airport;
  }

  async findNearest(
    lat: number,
    lon: number,
    limit = 3,
    maxDistanceKm = 300
  ): Promise<Airport[]> {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lon: lon.toString(),
      limit: limit.toString(),
      maxDistanceKm: maxDistanceKm.toString()
    });
    
    const res = await apiFetch(`/api/admin/airports/nearest?${params}`);
    if (!res.ok) throw new Error('Failed to find nearest airports');
    const data = await res.json();
    return data.data.airports as Airport[];
  }

  async exportCSV(params: URLSearchParams): Promise<Response> {
    const response = await apiFetch(`/api/admin/airports/export.csv?${params}`);
    if (!response.ok) throw new Error('Failed to export CSV');
    return response;
  }
}